'use client';

import { Breadcrumbs, Container, Link, Typography } from '@mui/material';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';

const BASE_URL = process.env.NEXT_PUBLIC_CLIENT_URL || 'https://www.lend.com.au';

const formatSegment = (segment: string) => {
  return segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const pathCache = new Map<string, boolean>();

async function checkPathExists(path: string): Promise<boolean> {
  if (pathCache.has(path)) {
    return pathCache.get(path)!;
  }

  try {
    const response = await fetch(`/api/check-path?path=${encodeURIComponent(path)}`);
    if (!response.ok) {
      pathCache.set(path, false);
      return false;
    }
    const data = await response.json();
    const exists = data.exists === true;
    pathCache.set(path, exists);
    return exists;
  } catch (error) {
    console.error('Error checking path existence:', error);
    pathCache.set(path, false);
    return false;
  }
}

export default function BreadcrumbsNav({ variant = 'light' }: { variant?: 'dark' | 'light' }) {
  const pathname = usePathname();
  const [pathExists, setPathExists] = useState<Record<number, boolean>>({});
  const checkedPathsRef = useRef<string>('');

  const pathSegments = useMemo(() => {
    if (!pathname) return [];
    return pathname.split('/').filter(Boolean);
  }, [pathname]);

  useEffect(() => {
    const currentPathKey = pathSegments.join('/');

    if (checkedPathsRef.current === currentPathKey) {
      return;
    }

    const checkPaths = async () => {
      setPathExists({});

      const pathChecks = pathSegments.map(async (_, idx) => {
        const path = `/${pathSegments.slice(0, idx + 1).join('/')}`;
        const exists = await checkPathExists(path);
        return { idx, exists };
      });

      const results = await Promise.all(pathChecks);

      const existsMap: Record<number, boolean> = {};
      results.forEach(({ idx, exists }) => {
        existsMap[idx] = exists;
      });

      setPathExists(existsMap);
      checkedPathsRef.current = currentPathKey;
    };

    if (pathSegments.length > 0) {
      checkPaths();
    } else {
      setPathExists({});
      checkedPathsRef.current = '';
    }
  }, [pathname, pathSegments]);

  return (
    <Container
      maxWidth="lg"
      {...{ 'data-component': 'BreadcrumbsNav' }}
      sx={() => ({
        pl: { xs: 2, sm: 0 },
        pr: { xs: 2, sm: 0 },
        position: 'relative',
        zIndex: 5,
      })}
    >
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={(theme) => ({
          mb: 2,
          color: variant === 'light' ? theme.palette.common.black : theme.palette.common.white,
        })}
      >
        <Typography variant="caption">
          <Link underline="always" color="inherit" href={BASE_URL}>
            Home
          </Link>
        </Typography>

        {pathSegments.map((segment, idx) => {
          const label = formatSegment(segment);
          const href = `${BASE_URL}/${pathSegments.slice(0, idx + 1).join('/')}`;
          const exists = pathExists[idx] ?? false;

          // Make clickable with underline if page exists, otherwise plain text
          return (
            <Typography variant="caption" key={`${segment}-${idx}`}>
              {exists ? (
                <Link underline="always" color="inherit" href={href}>
                  {label}
                </Link>
              ) : (
                label
              )}
            </Typography>
          );
        })}
      </Breadcrumbs>
    </Container>
  );
}
